<template>
    <ValidationObserver ref="creditCard">
        <div class="ls-cc-grid grid ls-credit-card--ie-fix">
            <div class="grid__col-sm-7">
                <div class="grid grid--bleed">
                    <div class="grid__col-xs-12">
                        <label class="ls-label ls-margin--top" for="ccnumber">
                            Credit Card
                        </label>
                        <div class="ls-input__wrapper">
                            <ValidationProvider
                                v-slot="{ classes }"
                                ref="number"
                                rules="required|isCreditCard"
                            >
                                <span class="ls-input__icon" :class="classes" aria-hidden="true">
                                    <i class="fa fa-credit-card fa-fw"></i>
                                </span>
                                <ls-cleave
                                    id="ccnumber"
                                    v-model="number"
                                    v-ls-track.change="{
                                        target: 'order-form--credit-card-input',
                                    }"
                                    :options="{
                                        creditCard: true,
                                    }"
                                    name="ccnumber"
                                    autocomplete="cc-number"
                                    class="ls-input"
                                    maxlength="30"
                                    type="tel"
                                    :class="classes"
                                    @input="checkValidationAndUpdate('number')"
                                ></ls-cleave>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="grid__col-xs-7" style="padding-right: 2rem !important;">
                        <label class="ls-label ls-margin--top" for="cc-exp">
                            Exp Date
                        </label>
                        <div
                            id="cc-exp"
                            class="ls-input__wrapper ls-credit-card--exp-date"
                            autocomplete="cc-exp"
                        >
                            <div
                                class="ls-input__wrapper for-select ls-credit-card--exp-date-input"
                            >
                                <ValidationProvider
                                    v-slot="{ classes }"
                                    ref="exp_month"
                                    name="exp_month"
                                    rules="required|exp_month:@exp_year"
                                >
                                    <select
                                        id="exp_month"
                                        v-model="exp_month"
                                        v-ls-track.change="{
                                            target: 'order-form--credit-card-exp-month',
                                            attachValue: true,
                                        }"
                                        class="ls-input"
                                        :class="classes"
                                        @change="checkValidationAndUpdate('exp_month')"
                                    >
                                        <option value="" selected disabled>MM</option>
                                        <option
                                            v-for="month in expMonthOptions"
                                            :key="month.value"
                                            :value="month.value"
                                        >
                                            {{ month.display }}
                                        </option>
                                    </select>
                                </ValidationProvider>
                            </div>
                            <div class="ls-credit-card--exp-date-slash">/</div>
                            <div
                                class="ls-input__wrapper for-select ls-credit-card--exp-date-input"
                            >
                                <ValidationProvider
                                    v-slot="{ classes }"
                                    ref="exp_year"
                                    name="exp_year"
                                    rules="required|exp_year:@exp_month"
                                >
                                    <select
                                        id="exp_year"
                                        v-model="exp_year"
                                        v-ls-track.change="{
                                            target: 'order-form--credit-card-exp-year',
                                            attachValue: true,
                                        }"
                                        class="ls-input"
                                        :class="classes"
                                        @change="checkValidationAndUpdate('exp_year')"
                                    >
                                        <option value="" selected disabled>YY</option>
                                        <option
                                            v-for="year in expYearOptions"
                                            :key="year.value"
                                            :value="year.value"
                                        >
                                            {{ year.display }}
                                        </option>
                                    </select>
                                </ValidationProvider>
                            </div>
                        </div>
                    </div>
                    <div class="grid__col-xs-5">
                        <label class="ls-label ls-margin--top" for="cvv2">
                            CVC
                            <span class="ls-credit-card--helper-text cc-cvc-location-back"
                                >(code on back)</span
                            >
                        </label>
                        <div class="ls-input__wrapper">
                            <ValidationProvider
                                v-slot="{ classes }"
                                ref="cvc"
                                rules="required|min:3"
                            >
                                <span class="ls-input__icon" :class="classes" aria-hidden="true">
                                    <i class="fa fa-lock fa-fw"></i>
                                </span>
                                <input
                                    id="cvv2"
                                    v-model="cvc"
                                    v-ls-track.change="{
                                        target: 'order-form--credit-card-cvc',
                                    }"
                                    name="cvv2"
                                    autocomplete="cc-csc"
                                    class="ls-input"
                                    maxlength="4"
                                    type="tel"
                                    :class="classes"
                                    @input="checkValidationAndUpdate('cvc')"
                                />
                            </ValidationProvider>
                        </div>
                    </div>
                </div>
            </div>
            <div class="grid__col-sm-5">
                <div class="ls-credit-card--all-cards ls-padding--top">
                    <span class="ls-credit-card--helper-text">All major credit cards accepted</span>
                    <div>
                        <i class="fa fa-2x fa-cc-mastercard"></i>&nbsp;
                        <i class="fa fa-2x fa-cc-visa"></i>&nbsp;
                        <i class="fa fa-2x fa-cc-amex"></i>&nbsp;
                        <i class="fa fa-2x fa-cc-discover"></i>
                    </div>
                </div>
            </div>
        </div>
    </ValidationObserver>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { mapActions, mapState } from 'vuex';
import * as types from '@/store/types';
import eventBusService from '@/services/event-bus.service';
import lsCleave from '@/components/original-mydot/inputs/cleave/Cleave.vue';

const currentDate = moment().toDate();
const currentYear = currentDate.getFullYear() - 2000;

export default {
    components: {
        lsCleave,
    },
    data() {
        return {
            number: '',
            exp_month: '',
            exp_year: '',
            cvc: '',
        };
    },
    computed: {
        ...mapState(['creditCard']),
        expMonthOptions() {
            const months = [];
            // eslint-disable-next-line no-plusplus
            for (let i = 1; i <= 12; i++) {
                months.push({
                    display: (i < 10 ? '0' : '') + i,
                    value: i,
                });
            }
            return months;
        },
        expYearOptions() {
            const years = [];
            // eslint-disable-next-line no-plusplus
            for (let i = currentYear; i <= currentYear + 20; i++) {
                years.push({
                    display: (i < 10 ? '0' : '') + i,
                    value: i,
                });
            }
            return years;
        },
    },
    created() {
        this.number = _.get(this.creditCard, 'card.number') || this.number;
        this.exp_month = _.get(this.creditCard, 'card.exp_month') || this.exp_month;
        this.exp_year = _.get(this.creditCard, 'card.exp_year') || this.exp_year;
        this.cvc = _.get(this.creditCard, 'card.cvc') || this.cvc;
    },
    mounted() {
        eventBusService.eventBus.$on('submitButtonClick', () => {
            if (this.$refs.creditCard) {
                this.$refs.creditCard.validate();
            }
        });
    },
    methods: {
        ...mapActions({
            updateCCValidation: types.CREDIT_CARD__UPDATE_CC_VALIDATION,
            updateCCNumber: types.CREDIT_CARD__UPDATE_CC_NUMBER,
            updateCCExpiration: types.CREDIT_CARD__UPDATE_CC_EXPIRATION,
            updateCCCVC: types.CREDIT_CARD__UPDATE_CC_CVC,
        }),
        checkValidationAndUpdate(ref) {
            this.$nextTick(() => {
                this.$refs.creditCard.validate({ silent: true }).then((valid) => {
                    this.updateCCValidation(valid);
                });
                this.$refs[ref].validateSilent().then(({ valid }) => {
                    if (!valid) {
                        return;
                    }

                    if (ref === 'number') {
                        this.updateCCNumber({ number: this.number });
                    } else if (ref === 'cvc') {
                        this.updateCCCVC({ cvc: this.cvc });
                    } else {
                        this.$refs[ref === 'exp_month' ? 'exp_year' : 'exp_month']
                            .validateSilent()
                            // eslint-disable-next-line no-shadow
                            .then(({ valid }) => {
                                if (valid) {
                                    this.updateCCExpiration({
                                        exp_month: this.exp_month,
                                        exp_year: this.exp_year,
                                    });
                                }
                            });
                    }
                });
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.ls-credit-card--helper-text {
    font-weight: normal;
    font-size: rem(-3);
    color: palette(gray, light);
}

.ls-credit-card--all-cards {
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    color: palette(gray, light);

    > div {
        padding-top: rem(-5);
    }
}

.ls-credit-card--exp-date {
    display: flex;
    align-items: center;
}

.ls-credit-card--exp-date-input {
    flex: 1 0 45%;
}

.ls-credit-card--exp-date-slash {
    flex: 1 0 10%;
    font-size: rem(3);
    padding: 0 5px;
    text-align: center;
}

.ls-credit-card--ie-fix {
    @include min-screen(breakpoint(sm)) {
        max-width: 644px;
    }
}
</style>
